import { readJson, writeStorage } from '@/lib/storage';

export type Environment = 'test' | 'live';

/**
 * The bearer credential for this tab. Kept in memory and mirrored to
 * sessionStorage (per tab, cleared with the tab) — never localStorage.
 */
export interface StoredCredential {
  token: string;
  expiresAt: string | null;
  tenantId: string;
  environment: Environment;
}

const STORAGE_KEY = 'biller.session';

function isCredential(value: unknown): value is StoredCredential {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.token === 'string' &&
    typeof v.tenantId === 'string' &&
    (v.environment === 'test' || v.environment === 'live') &&
    (v.expiresAt === null || typeof v.expiresAt === 'string')
  );
}

let current: StoredCredential | null = readJson('session', STORAGE_KEY, isCredential);
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export const tokenStore = {
  get(): StoredCredential | null {
    return current;
  },

  token(): string | null {
    return current?.token ?? null;
  },

  set(credential: StoredCredential): void {
    current = credential;
    writeStorage('session', STORAGE_KEY, JSON.stringify(credential));
    emit();
  },

  clear(): void {
    current = null;
    writeStorage('session', STORAGE_KEY, null);
    emit();
  },

  /** Cache scope: query keys are prefixed with it so test/live/tenant data never mix. */
  scopeId(): string {
    return current ? `${current.tenantId}:${current.environment}` : 'anonymous';
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
